import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Loading from '../components/Loading';
import Toast from '../components/Toast';
import ErrorToast from '../components/ErrorToast';
import FiltroImoveis from '../components/FiltroImoveis';
import Paginacao from '../components/Paginacao';
import { FaEye } from 'react-icons/fa';
import '../styles/Common.css';
import './BuscarProprietarios.css';

const BuscarProprietarios = () => {
  const [filters, setFilters] = useState({});
  const [imoveis, setImoveis] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItens, setTotalItens] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [selectedImoveis, setSelectedImoveis] = useState(new Set());
  const shouldSelectAllRef = useRef(false);

  const buscarImoveis = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const resposta = await api.get('/imoveis', {
        params: { ...filters, page, pageSize },
      });
      const { data, total } = resposta.data;
      setImoveis(data);
      setTotalItens(total);
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao buscar imóveis.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    buscarImoveis();
  }, [buscarImoveis]);

  useEffect(() => {
    if (shouldSelectAllRef.current) {
      const allIds = imoveis.map(imovel => imovel.id);
      setSelectedImoveis(new Set(allIds));
      shouldSelectAllRef.current = false;
    }
  }, [imoveis]);

  const handleSearch = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
    setPageSize(10);
  };

  const handleSelectImovel = (imovelId) => {
    const newSelected = new Set(selectedImoveis);
    if (newSelected.has(imovelId)) {
      newSelected.delete(imovelId);
    } else {
      if (newSelected.size < 1000) {
        newSelected.add(imovelId);
      } else {
        setError('Você pode selecionar no máximo 1000 imóveis.');
      }
    }
    setSelectedImoveis(newSelected);
  };

  const handleToggleAll = () => {
    if (selectedImoveis.size === totalItens) {
      setSelectedImoveis(new Set());
      setPageSize(10);
    } else {
      if (totalItens < 1000) {
        shouldSelectAllRef.current = true;
        setPageSize(totalItens);
      } else {
        setIsLoading(true);
        api.get('/imoveis', {
          params: { ...filters, all_ids: true, limit: 1000 },
        }).then(response => {
          const allIds = response.data.ids;
          setSelectedImoveis(new Set(allIds));
        }).catch(err => {
          setError(err.response?.data?.error || 'Erro ao buscar todos os IDs de imóveis.');
        }).finally(() => {
          setIsLoading(false);
        });
      }
    }
  };

  const handleSubmit = async () => {
    if (selectedImoveis.size === 0) {
      setError('Nenhum imóvel selecionado.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setToastMessage('');

    try {
      await api.post('/processamentos/buscar_proprietarios', {
        imovel_ids: Array.from(selectedImoveis),
      });
      setToastMessage('Processamento iniciado com sucesso! Você será notificado quando terminar.');
      setSelectedImoveis(new Set());
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao iniciar processamento.';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container buscar-proprietarios-container">
      <h1 className="page-title">Buscar Proprietários de Imóveis</h1>
      <p>Selecione os imóveis para os quais deseja buscar e vincular os proprietários mais recentes.</p>

      <div className="filters-section">
        <FiltroImoveis onSearch={handleSearch} />
      </div>

      {error && <ErrorToast message={error} onClose={() => setError('')} />}
      {toastMessage && <Toast message={toastMessage} onClose={() => setToastMessage('')} />}

      <div className="action-bar">
        <button onClick={handleSubmit} className="button-primary" disabled={isSubmitting || selectedImoveis.size === 0}>
          {isSubmitting ? 'Processando...' : `Importar Imóveis (${selectedImoveis.size})`}
        </button>
        <span className="selection-count">{selectedImoveis.size} de 1000 imóveis selecionados.</span>
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      onChange={handleToggleAll}
                      checked={selectedImoveis.size === totalItens && totalItens > 0}
                      ref={input => {
                        if (input) {
                          input.indeterminate = selectedImoveis.size > 0 && selectedImoveis.size < totalItens;
                        }
                      }}
                    />
                  </th>
                  <th>Detalhes do Imóvel</th>
                  <th>Situação</th>
                  <th>Proprietários</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {imoveis.map((imovel) => (
                  <tr key={imovel.id} className={selectedImoveis.has(imovel.id) ? 'selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedImoveis.has(imovel.id)}
                        onChange={() => handleSelectImovel(imovel.id)}
                      />
                    </td>
                    <td>
                      <div className="imovel-details">
                        <a href={`/imoveis/${imovel.id}`} target="_blank" rel="noopener noreferrer">
                          <strong>ID:</strong> {imovel.id}
                        </a>
                        <span><strong>Endereço:</strong> {`${imovel.logradouro}, ${imovel.numero} - ${imovel.complemento || ''} - ${imovel.bairro}, CEP: ${imovel.cep}`}</span>
                        {imovel.condominio && <span><strong>Condomínio:</strong> {imovel.condominio.nome}</span>}
                        {imovel.etiqueta && <span><strong>Etiqueta:</strong> {imovel.etiqueta.nome}</span>}
                      </div>
                    </td>
                    <td>{imovel.status}</td>
                    <td>
                      <ul className="proprietarios-list">
                        {imovel.proprietario_atual?.id ? (
                          <li key={imovel.proprietario_atual.id}>
                            <Link to={`/clientes/${imovel.proprietario_atual.id}`} target="_blank" rel="noopener noreferrer">
                              {imovel.proprietario_atual.nome}
                            </Link>
                          </li>
                        ) : (
                          <li>Nenhum proprietário associado</li>
                        )}
                      </ul>
                    </td>
                    <td className="action-icons">
                      <button
                        className="icon-button"
                        title="Detalhes"
                        onClick={() => window.open(`/imoveis/${imovel.id}`, '_blank')}
                      >
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Paginacao
            totalItens={totalItens}
            itensPorPagina={pageSize}
            paginaAtual={page}
            setPaginaAtual={setPage}
          />
        </>
      )}
    </div>
  );
};

export default BuscarProprietarios;